<script setup lang="ts">
import type { Ref } from 'vue';
import { DOCUMENT } from '@pkg/config/sanity/schemas';
import { IMAGE_SIZES } from '@pkg/ui/constants/imageSizes';
import { useI18n } from 'vue-i18n';
import { getExcerpt } from '~/app/helpers/getExcerpt';
import { useLocalisedDate } from '~/app/composables/useLocalisedDate';
import { useListingContent } from '~/app/composables/useListingContent';
import { usePaginationMetadata } from '~/app/composables/usePaginationMetadata';
import type { ListingPost } from '~/app/types/listingTypes';
import { LISTING_TYPES } from '~/app/types/listingTypes';
import { useSiteManifestStore } from '~/app/stores/siteManifest';
import { useListingCollectionJsonLd } from '~/app/composables/useListingCollectionJsonLd';

const props = defineProps<{
  listingDocument?: string;
  postsPerPage: number;
  listingFilter?: string;
  listingType?: string;
  categorySelection?: Array<string>;
  sanityKey?: string;
}>();

const siteManifestStore = useSiteManifestStore();

const listingBlock: Ref<HTMLDivElement | null> = ref(null);
const activeFilter = props.categorySelection && props.listingType === LISTING_TYPES.CATEGORY ? props.categorySelection : null;

const { data, params, handleFilter, listingKey } = await useListingContent(listingBlock, {
  type: props.listingDocument,
  postsPerPage: props.postsPerPage,
  taxonomy: props.listingFilter,
  activeFilter,
  sanityKey: props.sanityKey,
});

useListingCollectionJsonLd(computed(() => ({
  listingDocument: props.listingDocument,
  listingType: props.listingType,
  posts: data.value?.posts,
})));

// Expose pagination data for metadata
const { generatePaginationLinks, handlePagination } = usePaginationMetadata();

// Generate pagination metadata links when data changes
watchEffect(() => {
  if (data.value?.totalPosts && props.postsPerPage && props.listingType !== LISTING_TYPES.LATEST) {
    const links = generatePaginationLinks(data.value.totalPosts, props.postsPerPage, listingKey);
    if (links.length > 0) {
      useHead({ link: links });
    }
  }
});

// Handle pagination with scroll to top
const handlePaginationWithScroll = (selectedPage: number) => {
  handlePagination(selectedPage, listingKey, listingBlock.value);
};

const isListingType = computed(() => props.listingType !== LISTING_TYPES.LATEST);
const showListingFilter = computed(() => props.listingFilter && isListingType.value && props.listingType !== LISTING_TYPES.CATEGORY);

function handlePostLink(post: ListingPost) {
  if (post?.file?.url) {
    return post?.file?.url + '?=dl';
  } else if (post?.url) {
    return post?.url;
  } else {
    const localePath = useLocalePath();
    return localePath(post.slug);
  }
}

function handleOpenPost(post: ListingPost) {
  return post?.file?.url || post?.url ? '_blank' : '_self';
}

function handleActionText(post: ListingPost) {
  const { t } = useI18n();
  return post?.file?.url ? t('download') : '';
}

function handlePublishedDate(post: ListingPost) {
  return post.publishedAt ? useLocalisedDate(post.publishedAt) : '';
}

function handleDescription(post: ListingPost) {
  if (props.listingDocument === DOCUMENT.PRESS_RELEASES) { return; }

  return getExcerpt(post);
}

const isMediaAssetsType = computed(() => props.listingDocument === DOCUMENT.PRESS_MEDIA_ASSETS);

function handlePostImage(post: ListingPost) {
  if (post && !post?.image?.asset) {
    switch (props.listingDocument) {
      case DOCUMENT.ABOUT_FOOD_WASTE_RESOURCES:
        return siteManifestStore?.fallbackImages?.resource;

      case DOCUMENT.PRESS_RELEASES:
      case DOCUMENT.ABOUT_FOOD_WASTE_BLOG:
        return siteManifestStore?.fallbackImages?.press;

      default:
        return null;
    }
  }

  return post.image;
}

</script>

<template>
  <div ref="listingBlock" :class="['c-listing', { 'c-listing--thumbnail': !!isMediaAssetsType }]">
    <div v-if="showListingFilter" class="c-listing__filter text-center">
      <BaseFilter
        :taxonomy="props.listingFilter"
        :sanity-key="props.sanityKey"
        :listing-type="props.listingDocument"
        :posts-per-page="props.postsPerPage"
        @update:filter="handleFilter"
      />
    </div>
    <div
      v-if="data?.posts && data?.posts.length > 0"
      :class="['c-listing__posts', {'c-listing__posts--media': !!isMediaAssetsType }]"
      :data-layout="props.listingType"
    >
      <a
        v-for="post in data?.posts"
        :key="post._id"
        :data-id="post._id"
        :href="handlePostLink(post)"
        :title="post.title"
        :target="handleOpenPost(post)"
      >
        <BaseTile
          :title="post.title"
          :pre-title="post.pretitle"
          :description="handleDescription(post)"
          :date="handlePublishedDate(post)"
          :tag="post.categories && !isMediaAssetsType ? post.categories[0]?.title : ''"
          :size="isMediaAssetsType ? 'media' : 'stretch'"
          :action-text="handleActionText(post)"
          :action-type="isMediaAssetsType ? 'default' : 'text'"
        >
          <template #image>
            <BaseImage
              v-if="handlePostImage(post)"
              :image="handlePostImage(post)"
              auto="format"
              :height="isMediaAssetsType ? 200 :undefined"
              :width="isMediaAssetsType ? 200 : IMAGE_SIZES.MAX_IMG_WIDTH_THUMBNAIL"
            />
          </template>
        </BaseTile>
      </a>
    </div>

    <div v-else class="flex justify-center text-center">
      <h3 class="c-listing__no-posts-title text-h3">
        {{ $t('noPostsFound') }}
      </h3>
    </div>

    <div v-if="data?.totalPosts && data?.totalPosts > props.postsPerPage && isListingType" class="c-listing__pagination text-center">
      <BasePagination
        v-model="params.page"
        :total-posts="data?.totalPosts"
        :items-per-page="props.postsPerPage"
        :listing-key="listingKey"
        @update:page="handlePaginationWithScroll"
      />
    </div>
  </div>
</template>

<style scoped>
/**
* When pagination is clicked, the user is scrolled to the top of the listing block.
* We add a scroll-margin-top total of the height of the header, as the header overlays the page + 20%
* of it's height so the top of the block isn't sitting directly on the bottom of the header when scrolled up.
*/
.c-listing {
  --listing-grid-gap: theme('spacing.24');
  scroll-margin-top: calc(var(--header-height) * 1.2);

  &:deep(.c-base-image) {
    padding: 12px 12px 0 12px;
    object-position: center;
  }

  &--thumbnail {
    &:deep(.c-tile--media .c-tile__image) {
      height: auto;
    }

    &:deep(.c-tile__image img) {
    padding: 8px;
    max-height: 160px;
    max-width: 160px;
    object-position: center;
    object-fit: cover;
  }
  }

  &:deep(.c-tile__title) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 4;
  }

  &:deep(.c-tile__content) {
    text-align: left;
    align-items: flex-start;
  }
}

.c-listing__posts {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--listing-grid-gap);

  @screen tablet {
    grid-template-columns: repeat(2, 1fr);
  }

  @screen desk {
    grid-template-columns: repeat(4, 1fr);
  }

  &--media {

    :deep(.c-tile__content) {
      text-align: center;
      align-items: center;
    }

    @screen desk {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &[data-layout="latest"] {
    grid-template-columns: repeat(1, 1fr);

    @screen tablet {
      grid-template-columns: repeat(2, 1fr);
    }

    @screen desk {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}

.c-listing__no-posts-title {
  color: var(--theme-heading-color);
}
</style>
